/**
 * 콜랩 상세 목 데이터 — `GET /web/v3/collabs/{id}` 모양(목 모드 `NEXT_PUBLIC_API_MOCK=1` 전용).
 * @description 어느 id 로 열어도 같은 콜랩을 그 id 로 돌려준다(목 목록의 카드가 모두 열리게).
 *   카드 · 스레드 · 작품이 한 화면에 다 서도록 모금 카드와 작품 카드를 붙였다. 날짜는 지금 기준
 *   상대 시각이라 언제 열어도 진행 중이다. 그림은 `/public` 의 것만 쓴다 — 바깥 네트워크를 타지 않는다.
 */
import type {
  CollabV3DetailDto,
  CollabV3ThreadDto,
  CollabV3WorksDto,
} from "@/domains/core/collab";

const DAY_MS = 86_400_000;
const daysFromNow = (days: number) =>
  new Date(Date.now() + days * DAY_MS).toISOString();

const COVER = "/assets/mock/birthday-cafe.webp";
const WORKS = ["wall-a.jpg", "wall-1.webp", "wall-c.jpg", "wall-2.png"].map(
  (file) => `/assets/mock/${file}`,
);
const host = {
  userId: 1,
  username: "nova_ads",
  displayName: "Nova Ad Team",
  profileImageUrl: COVER,
};
const fan = (userId: number, displayName: string) => ({
  userId,
  username: `fan${userId}`,
  displayName,
});

export const mockCollabV3Detail = (collabId: number): CollabV3DetailDto => ({
  collabId,
  version: "V3",
  status: "PUBLISHED",
  statusBadge: "IN_PROGRESS",
  listingVisibility: "PUBLIC",
  isOperatorProvided: false,
  title: "Karina's Birthday Cafe",
  description:
    "A café near Gwanghwamun for Karina's day.\nCup sleeves, a photo wall and a birthday ad at the station.",
  descriptionImageUrls: [COVER],
  cover: { imageUrl: COVER },
  artist: { artistId: 1, artistName: "aespa" },
  organizer: { ...host, accountType: "PERSONA", avatarShape: "SQUARE" },
  startAt: daysFromNow(-1),
  endAt: daysFromNow(5),
  location: {
    isOnline: false,
    city: "Jongno District",
    state: "Seoul",
    country: { countryCode: "KR", countryName: "South Korea" },
    locationInstruction: "Exit 3, Gwanghwamun Station",
  },
  participantCount: 12,
  participantsPreview: [host, fan(2, "Hyunjin"), fan(3, "Noor")].map(
    (user, index) => ({ user, isOrganizer: index === 0, joinedAt: daysFromNow(-3) }),
  ),
  cards: {
    fee: null,
    funding: {
      status: "OPEN",
      purpose: "Birthday ad at Gwanghwamun Station",
      goalAmount: 5000,
      endAt: daysFromNow(3),
      collectedAmount: 3200,
      progressPercent: 64,
      supporterCount: 42,
      supportersPreview: [fan(2, "Hyunjin"), fan(3, "Noor"), fan(4, "Mina")],
    },
    works: {
      deadline: daysFromNow(4),
      workCount: WORKS.length,
      uploaderCount: WORKS.length,
      allowMultipleWorks: false,
      hasAnnounced: false,
    },
  },
  checkin: null,
  selectedWorks: [],
  viewer: { isOrganizer: false, isParticipant: false },
  shareImage: null,
  createdAt: daysFromNow(-7),
  updatedAt: daysFromNow(-1),
});

const item = (itemId: number, content: string, imageUrls: string[] = []) => ({
  itemType: "POST" as const,
  itemId,
  author: { ...host, isOrganizer: true },
  content,
  imageUrls,
  likeCount: 8,
  commentCount: 3,
  createdAt: daysFromNow(-itemId / 10),
});

export const MOCK_COLLAB_V3_THREAD: CollabV3ThreadDto = {
  pinned: [item(1, "The café opens at 11. Bring your cup sleeve!", [COVER])],
  data: [item(2, "Photo wall is up 📸", WORKS.slice(0, 3)), item(3, "Thank you all!")],
  nextCursor: "mock",
};

export const MOCK_COLLAB_V3_WORKS: CollabV3WorksDto = {
  data: WORKS.map((url, index) => ({
    workId: 100 + index,
    author: fan(index + 2, `Fan ${index + 1}`),
    imageUrls: [url],
    isSelected: index === 0,
  })),
  totalCount: WORKS.length,
};
